import { useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import AppLayout from '../../components/AppLayout';
import { eventKeys, useEvent } from '../../queries/EventQueries';
import { format } from 'date-fns';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import EventTable from '../../components/EventTable';
import AddAlpacaToEvent from '../../components/AddAlpacaToEvent';
import AlpacasForEventTable from '../../components/AlpacasForEventTable';
import EventApi from '../../apis/event_api';
import { handleError } from '../../apis';
import { toast } from 'react-toastify';
import ShowPending from '../../components/ShowPending';

export default function EventPage() {
  const { id } = useParams();
  const eventId = id ? +id : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isOpen = searchParams.get('isOpen') || 'false';
  const { data: event } = useEvent(eventId);

  const handleAlpacaEventDelete = async (alpacaEventId: number) => {
    if (!alpacaEventId || !eventId) {
      navigate(`/events/${eventId}`);
    }
    const proceed = window.confirm(
      'Deleting an Alpaca Event can not be undone. Are you sure?',
    );
    if (!proceed) return;
    try {
      await EventApi.deleteAlpacaEvent(+eventId, +alpacaEventId);
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  const handleEventDelete = async () => {
    if (!eventId) {
      return;
    }
    const proceed = window.confirm(
      'Deleting an Event can not be undone. Are you sure?',
    );
    if (!proceed) return;
    try {
      await EventApi.deleteEvent(+eventId);
      queryClient.removeQueries({ queryKey: eventKeys.byId(eventId) });
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      return navigate('/events');
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  if (!event) {
    return (
      <AppLayout>
        <ShowPending isPending={!event} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className='w-full'>
        <div className='flex items-start justify-center'>
          <h3 className='text-center text-2xl dark:text-gray-300'>
            {format(new Date(event?.date ?? ''), 'MM/dd/yyyy')}{' '}
            {event?.eventTypeId == 7
              ? event.otherEventName
              : event?.eventType?.eventTypeName}
          </h3>
          <Link
            to={`/update-event/${event?.eventId}`}
            className='ml-4 p-1 text-xs font-bold uppercase hover:opacity-70'
          >
            <div className='flex items-center gap-2'>
              <FaEdit /> Edit
            </div>
          </Link>
        </div>

        <div className='pt-4'>
          {event && <EventTable events={event ? [event] : []} showActions />}
        </div>
        <div className='pt-4' key={event?.alpacasForEvent?.length.toString()}>
          <AddAlpacaToEvent isOpen={isOpen} event={event} />
          {event?.alpacasForEvent && event?.alpacasForEvent?.length > 0 && (
            <AlpacasForEventTable
              events={event?.alpacasForEvent}
              handleDelete={handleAlpacaEventDelete}
            />
          )}
        </div>

        <div className='flex justify-center p-4'>
          <button
            className='my-2  rounded-md border border-rose-600 bg-rose-500 p-2 text-xs font-bold uppercase text-white hover:border-rose-700 hover:bg-rose-600 dark:border-rose-500 dark:bg-rose-500  dark:hover:border-rose-600  dark:hover:bg-rose-600 '
            type='button'
            title={`Delete Event`}
            onClick={handleEventDelete}
          >
            <div className='flex items-center gap-2'>
              <FaTrashAlt /> Event
            </div>
          </button>
        </div>
        <Outlet context={event} />
      </div>
    </AppLayout>
  );
}
